import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import { useFetch } from "@/hooks/useFetch";
import { ENDPOINTS } from "@/const/endpoints";
import { isNetworkError } from "@/utils/networkError";

export function useResendVerification() {
  const { t } = useLang();
  const client = useFetch();
  const [cooldown, setCooldown] = useState(0);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setInterval(() => {
      setCooldown((c) => (c <= 1 ? 0 : c - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [cooldown]);

  const resend = useCallback(async () => {
    if (cooldown > 0 || sending) return;
    setSending(true);
    try {
      await client.post(ENDPOINTS.AUTH_RESEND_VERIFICATION);
      toast.success(t("email_verify_resent"));
      setCooldown(180);
    } catch (err) {
      if (isNetworkError(err)) {
        return;
      }
      const retryAfter = err?.response?.data?.retryAfterSeconds;
      if (err?.response?.status === 429 && typeof retryAfter === "number" && retryAfter > 0) {
        setCooldown(retryAfter);
        toast.message(`${t("email_verify_wait")} ${retryAfter}s`);
      } else {
        const msg =
          err?.response?.data?.error || err?.message || t("email_verify_resend_error");
        toast.error(msg);
      }
    } finally {
      setSending(false);
    }
  }, [client, cooldown, sending, t]);

  return { cooldown, sending, resend };
}

export function needsEmailVerification(user) {
  return Boolean(user?.email) && user.emailVerified !== true;
}
